 
import { Box, CircularProgress, Grid, Link, MenuItem } from "@mui/material";
import { useFormik } from "formik";
import React from "react";
import {
  ControlledCustomSelect,
  CustomFormLabel,
  CustomSelect,
  CustomTextField,
} from "../../../../../../../components/formsComponents";
import {
  isAlphaNumeric,
  renderDropdownValue,
} from "../../../../../../../helpers";
import { useDispatchWrapper, useCustomerPolicyDetails } from "../../../../../../../hooks";
import { useSelector } from "react-redux";
import { IStoreState } from "../../../../../../../redux/initialStoreState";
import moment from "moment";
import { LabelCard } from "../../../../../../../components/LabelCard/LabelCard";
import {
  addAutomobileVehicleAsync,
  fetchAutomobileVehicleAsync,
} from "../../../../../../../redux/automobileVehicle/automobileVehicleActions";
import { CustomDatePicker } from "../../../../../../../components/formsComponents/CustomDatePicker";
import { PageLoader } from "../../../../../../../components/PageLoader/PageLoader";
import { LoadState, MODULE_IDS } from "../../../../../../../constants/enums";


import { CurrencyTextField } from "../../../../../../../components/CurrencyTextField/CurrencyTextField";
import {
  COMMON_STATUS,
  product_type_options,
  type_code_options,
  VEHICLE_VALIDATION_MESSAGES,
} from "../../../../../../../constants/constants";
import { RoleBasedCustomButton } from "../../../../../../../security/RoleBasedComponents/RoleBasedCustomButton/RoleBasedCustomButton";
import { ExtendableSelect } from "../../../../../../../components/formsComponents/ExtendableSelect";
import {
  IAutomobileVehicleInformation,
} from "../../../../../../../redux/automobileVehicle/automobileVehicle.types";
import { FieldSet } from "../../../../../../../components/FieldSet/FieldSet";
import { useAutomobilePolicyDriverInfo } from "../../hooks/useAutomobilePolicyDriverInfo";
import {
  AUTOMOBILE_CSIO_ENGINE_REVERSE,
  AUTOMOBILE_CSIO_NEWUSED_REVERSE,
  AUTOMOBILE_CSIO_VEHICLE_BODY_TYPE_REVERSE,
  AUTOMOBILE_CSIO_VEHICLE_TYPE,
  AUTOMOBILE_CSIO_VEHICLE_USE_REVERSE,
  CSIO_ANTI_THEFT_DEVICE_CODE_LIST,
  CSIO_ANTI_THEFT_ENGRAVING_DEV_MFR_CD,
} from "../../../../../../../constants/csioConstants";
import { IVinDecodeResponse } from "../../../../../../../redux/vinDecoder/vinDecoder.types";
import { decodeVinAsync } from "../../../../../../../redux/vinDecoder/vinDecoderActions";
import { showMessage } from "../../../../../../../redux/messages/messagesActions";
import { VerifyVinDialog } from "./VerifyVinDialog";

export const mapVinResponseToFields = (
  data: IVinDecodeResponse
): Record<string, string | undefined> => {
  const make = data.marketingMakes?.[0]?.description;
  const model = data.marketingMakes?.[0]?.models?.[0]?.description;
  const vehicleCode = data.marketingMakes?.[0]?.models?.[0]?.code;
  const bodyType = data.marketingMakes?.[0]?.models?.[0]?.products?.find(
    (p) => p.description === "CSIO Body Type Indicator"
  )?.code;

  return {
    year: data.yearValue,
    make,
    model,
    incremental_number: data.sequentialNumber,
    vehicle_code_one: vehicleCode,
    vehicle_type: data.vehicleType,
    body_type: bodyType,
  };
};

export const AutomobileVehicleInformation: React.FC<{
  customerId?: number;
  customerPolicyId?: number;
  vehicleNumber?: number;
  changeStep?: (value: number) => void;
  readOnly?: boolean;
  fromRightPanel?: boolean;
  showOnlySaveButton?: boolean;
  staticStatusValue?: string;
  created_from?: string;
  onSaveSuccess?: (data: IAutomobileVehicleInformation) => void;
  initialData?: IAutomobileVehicleInformation;
  disableApiCalls?: boolean;
}> = (props) => {
  const {
    customerId,
    customerPolicyId,
    vehicleNumber,
    readOnly = false,
    showOnlySaveButton = false,
    onSaveSuccess,
    staticStatusValue,
    created_from,
    initialData,
    disableApiCalls = false,
  } = props;

  const { VehicleInformation } = useSelector(
    (storeState: IStoreState) => storeState.automobile.vehicle
  );

  // In the policy-change flow this panel opens on top of a screen that already
  // loaded the customer-policy-details. Re-fetching here flips the shared
  // loading slice and remounts the parent form, so read-only consume it.
  const skipPolicyDetailsFetch = created_from === "Policy_Change";
  const { details: policyAdmin } = useCustomerPolicyDetails(
    customerPolicyId,
    skipPolicyDetailsFetch
  );

  const { driverDropDown } = useAutomobilePolicyDriverInfo(customerPolicyId);
  const dispatch = useDispatchWrapper();
  const [loading, setLoading] = React.useState(false);
  const [buttonClicked, setButtonClicked] = React.useState<string | null>(null);
  const [vinDecodeLoading, setVinDecodeLoading] = React.useState(false);
  const [openVerifyVin, setOpenVerifyVin] = React.useState(false);
  const {
    values,
    handleChange,
    handleSubmit,
    setValues,
    setFieldValue,
    setErrors,
    errors,
  } = useFormik({
    initialValues: initialData ? initialData : VehicleInformation?.data,

    validate: (values) => {
      if (readOnly) {
        return {};
      }
      const requiredFields: {
        key: keyof IAutomobileVehicleInformation;
        message: string;
      }[] = VEHICLE_VALIDATION_MESSAGES as {
        key: keyof IAutomobileVehicleInformation;
        message: string;
      }[];

      const errors: any = {};
      if (!values.vin) {
        errors.vin = "VIN is required field.";
      } else if (values.vin.length < 17 || values.vin.length > 17) {
        errors.vin = "VIN number must be 17 digits.";
      } else if (!isAlphaNumeric(values.vin)) {
        errors.vin = "VIN number must be alpha numeric.";
      }
      requiredFields.forEach(({ key, message }) => {
        if (!values[key]) {
          errors[key] = message;
        }
      });
      return errors;
    },
    onSubmit: (values) => {
      if (disableApiCalls && onSaveSuccess) {
        onSaveSuccess(values);
      } else {
        if (customerId && customerPolicyId) {
          let statusValue = values.status;
          if (staticStatusValue) {
            statusValue = staticStatusValue;
          }
          setLoading(true);
          dispatch(
            addAutomobileVehicleAsync(
              {
                ...values,
                customer_id: customerId,
                customer_policy_id: customerPolicyId,
                status: statusValue,
                created_from: created_from || "Vehicle",
              },
              (isSuccess, data) => {
                if (isSuccess && data) {
                  if (buttonClicked === "submit") {
                    if (onSaveSuccess && data) {
                      onSaveSuccess(data);
                    }
                  } else {
                    window.scrollTo(0, 0);
                    if (props.changeStep) {
                      props.changeStep(1);
                    }
                  }
                }

                setLoading(false);
              }
            )
          );
        }
      }
    },
  });

  React.useEffect(() => {
    if (initialData && disableApiCalls) return;

    setValues({
      ...VehicleInformation?.data,
      customer_name: policyAdmin?.data?.customer_name || "",
      policy_number: policyAdmin?.data?.policy_number || "",
    });
  }, [VehicleInformation?.data, initialData, disableApiCalls, policyAdmin]);

  React.useEffect(() => {
    if (customerPolicyId && vehicleNumber && !disableApiCalls) {
      dispatch(fetchAutomobileVehicleAsync(customerPolicyId, vehicleNumber));
    }
  }, [customerPolicyId, vehicleNumber]);

  const handleVinDecode = () => {
    const vin = values.vin;
    if (!vin || vin.length !== 17 || !isAlphaNumeric(vin)) return;

    setVinDecodeLoading(true);
    dispatch(
      decodeVinAsync(vin, (isSuccess, data) => {
        setVinDecodeLoading(false);
        if (isSuccess && data) {
          const fields = mapVinResponseToFields(data);
          Object.entries(fields).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              setFieldValue(key, value);
            }
          });
          // Clear validation errors for the populated fields
          setErrors({});
          dispatch(
            showMessage({
              type: "success",
              message: "VIN decoded successfully. Vehicle data has been populated.",
              displayAs: "snackbar",
            })
          );
        }
      })
    );
  };

  const handleVerifyVinUpdate = (
    fields: Record<string, string | undefined>
  ) => {
    Object.entries(fields).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        setFieldValue(key, value);
      }
    });
    setErrors({});
    dispatch(
      showMessage({
        type: "success",
        message: "Vehicle data has been updated from the decoded VIN.",
        displayAs: "snackbar",
      })
    );
  };

  return (
    <PageLoader
      loading={VehicleInformation.loading === LoadState.InProgress}
      error={
        VehicleInformation.error
          ? { message: VehicleInformation.error }
          : undefined
      }
    >
      <form onSubmit={handleSubmit}>
        <FieldSet disabled={readOnly}>
          <Grid container spacing={1}>
            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Vehicle Number</CustomFormLabel>
              <CustomTextField
                name="incremental_number"
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                value={values.incremental_number}
                disabled
              />
            </Grid>
            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Customer Name</CustomFormLabel>
              <CustomTextField
                name="customer_name"
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                value={values.customer_name}
                disabled
                //onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Unit Number</CustomFormLabel>
              <CustomTextField
                name="unit_number"
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                value={values.unit_number}
                onChange={handleChange}
              />
            </Grid>
              <Grid item xs={12} lg={2.4}>
              <Box display="flex" alignItems="baseline" justifyContent={'space-between'}>
                <CustomFormLabel>VIN</CustomFormLabel>
                {!readOnly && (
                  <Box display="flex" alignItems="baseline" gap={1.5}>
                    {vinDecodeLoading ? (
                      <CircularProgress size={16} sx={{ ml: 1 }} />
                    ) : (
                      <Link
                        component="button"
                        type="button"
                        variant="body1"
                        onClick={handleVinDecode}
                        sx={{ fontWeight: 500 }}
                      >
                        Decode VIN
                      </Link>
                    )}
                    <Link
                      component="button"
                      type="button"
                      variant="body1"
                      onClick={() => setOpenVerifyVin(true)}
                      sx={{ mr: 1, fontWeight: 500 }}
                    >
                      Verify VIN
                    </Link>
                  </Box>
                )}
              </Box>
              <CustomTextField
                name="vin"
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                value={values.vin}
                error={errors.vin ? true : false}
                helperText={errors.vin}
                inputProps={{ maxLength: 17 }}
                onChange={(e) =>
                  setFieldValue("vin", e.target.value.toUpperCase())
                }
              />
            </Grid>
            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Year</CustomFormLabel>
              <CustomDatePicker
                views={["year"]}
                fullWidth
                value={values.year}
                inputFormat="YYYY"
                onChange={(newValue, a, momentObject) => {
                  const newDate = moment(momentObject).format("YYYY");
                  setFieldValue("year", momentObject ? newDate : null);
                }}
                errorMessage={errors.year}
              />
            </Grid>

            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Make</CustomFormLabel>
              <CustomTextField
                name="make"
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                value={values.make}
                error={errors.make ? true : false}
                helperText={errors.make}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Model</CustomFormLabel>
              <CustomTextField
                name="model"
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                value={values.model}
                error={errors.model ? true : false}
                helperText={errors.model}
                onChange={handleChange}
              />
            </Grid>

          

            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Actual Cash Value</CustomFormLabel>
              <CurrencyTextField
                fullWidth
                value={values.acutal_cash_value}
                onChange={(value) => {
                  setFieldValue("acutal_cash_value", value);
                }}
              />
            </Grid>
            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>List Price New</CustomFormLabel>
              <CurrencyTextField
                fullWidth
                value={values.list_price_new}
                onChange={(value) => {
                  setFieldValue("list_price_new", value);
                }}
              />
            </Grid>

            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Owned</CustomFormLabel>
              <ControlledCustomSelect
                fullWidth
                value={values.owned}
                name="owned"
                onChange={handleChange}
                placeholder="Select"
                displayEmpty
                options={[
                  { label: "Yes", value: "Yes" },
                  { label: "No", value: "No" },
                ]}
                error={errors.owned ? true : false}
                helperText={errors.owned}
              />
            </Grid>

            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Leased</CustomFormLabel>
              <ControlledCustomSelect
                fullWidth
                value={values.leased}
                name="leased"
                onChange={handleChange}
                placeholder="Select"
                displayEmpty
                options={[
                  { label: "Yes", value: "Yes" },
                  { label: "No", value: "No" },
                ]}
                error={errors.leased ? true : false}
                helperText={errors.leased}
              />
            </Grid>

            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Purchase Date</CustomFormLabel>
              <CustomDatePicker
                fullWidth
                value={values.purchase_date || ""}
                onChange={(newValue) => {
                  setFieldValue("purchase_date", newValue);
                }}
                errorMessage={errors.purchase_date}
              />
            </Grid>

            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Purchase Condition</CustomFormLabel>
              <ControlledCustomSelect
                fullWidth
                value={values.puchase_condition}
                name="puchase_condition"
                onChange={handleChange}
                placeholder="Select one"
                displayEmpty
                options={AUTOMOBILE_CSIO_NEWUSED_REVERSE.map((template) => {
                  return { label: template, value: template };
                })}
                error={errors.puchase_condition ? true : false}
                helperText={errors.puchase_condition}
              ></ControlledCustomSelect>
            </Grid>

            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Purchase price ($)</CustomFormLabel>
              <CurrencyTextField
                fullWidth
                value={values.purchase_price}
                onChange={(value) => {
                  setFieldValue("purchase_price", value);
                }}
                error={errors.purchase_price ? true : false}
                helperText={errors.purchase_price}
              />
            </Grid>

            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Plate Number</CustomFormLabel>
              <CustomTextField
                name="plate_number"
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                value={values.plate_number}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Vehicle Code</CustomFormLabel>
              <CustomTextField
              id="vehicle_code_one"
              name="vehicle_code_one"
               value={values.vehicle_code_one}
               onChange={handleChange}
            
              error={errors.vehicle_code_one ? true : false}
              helperText={errors.vehicle_code_one}
              />
              
            </Grid>

            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Vehicle Code*</CustomFormLabel>
              <CustomTextField
              id="vehicle_code_two"
              name="vehicle_code_two"
               value={values.vehicle_code_one}
               onChange={handleChange}
            
            
              />
            
            </Grid>

            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Vehicle Type</CustomFormLabel>
              <ControlledCustomSelect
                variant="outlined"
                size="small"
                type="string"
                name="vehicle_type"
                fullWidth
                value={values.vehicle_type}
                onChange={handleChange}
                displayEmpty
                placeholder="Select one"
                options={AUTOMOBILE_CSIO_VEHICLE_TYPE.map((transactionType) => {
                  return { label: transactionType, value: transactionType };
                })}
                error={errors.vehicle_type ? true : false}
                helperText={errors.vehicle_type}
              ></ControlledCustomSelect>
            </Grid>

            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Body Type</CustomFormLabel>
              <ExtendableSelect
                placeholder="Select one"
                value={values.body_type}
                options={AUTOMOBILE_CSIO_VEHICLE_BODY_TYPE_REVERSE.map(
                  (policyType) => {
                    return { label: policyType, value: policyType };
                  }
                )}
                onChange={(newValue) => {
                  setFieldValue("body_type", newValue);
                }}
                errorMessage={errors.body_type}
                error={errors.body_type ? true : false}
              />
            </Grid>
            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Gross Vehicle Weight</CustomFormLabel>
              <CustomTextField
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                name="gross_vehicle_weight"
                value={values.gross_vehicle_weight}
                onChange={handleChange}
                error={errors.gross_vehicle_weight ? true : false}
                helperText={errors.gross_vehicle_weight}
              />
            </Grid>

            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Location Code</CustomFormLabel>
              <CustomTextField
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                name="location_code"
                value={values.location_code}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Fleet Identity</CustomFormLabel>
              <CustomTextField
                name="fleet_identity"
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                value={values.fleet_identity}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Anti-Lock Brakes</CustomFormLabel>
              <CustomTextField
                variant="outlined"
                size="small"
                type="string"
                fullWidth
                name="anti_lock_brakes"
                value={values.anti_lock_brakes}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Fuel Type</CustomFormLabel>
              <ControlledCustomSelect
                fullWidth
                value={values.fuel_type}
                name="fuel_type"
                onChange={handleChange}
                placeholder="Select one"
                displayEmpty
                options={AUTOMOBILE_CSIO_ENGINE_REVERSE.map((template) => {
                  return { label: template, value: template };
                })}
                error={errors.fuel_type ? true : false}
                helperText={errors.fuel_type}
              ></ControlledCustomSelect>
            </Grid>

            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Territory Code</CustomFormLabel>
              <CustomTextField
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                name="territory_code"
                value={values.territory_code}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Odometer</CustomFormLabel>
              <CustomTextField
                name="odometer"
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                value={values.odometer}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Purchase Country</CustomFormLabel>
              <CustomTextField
                name="purchase_country"
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                value={values.purchase_country}
                onChange={handleChange}
                error={errors.purchase_country ? true : false}
                helperText={errors.purchase_country}
              />
            </Grid>

            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Vehicle Registration Province</CustomFormLabel>
              <ControlledCustomSelect
                fullWidth
                value={values.vehicle_registration_province}
                name="vehicle_registration_province"
                onChange={handleChange}
                placeholder="Select one"
                displayEmpty
                options={[
                      "AB",
                      "BC",
                      "MB",
                      "NB",
                      "NL",
                      "NS",
                      "NT",
                      "NU",
                      "ON",
                      "PE",
                      "QC",
                      "SK",
                      "YT",
                    ].map((template) => {
                  return { label: template, value: template };
                })}
                error={errors.vehicle_registration_province ? true : false}
                helperText={errors.vehicle_registration_province}
              ></ControlledCustomSelect>
            </Grid>
            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Ownership of Vehicle</CustomFormLabel>
              <CustomTextField
                id="ownership"
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                value={values.ownership}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Owner Operator or Lessee</CustomFormLabel>
              <ControlledCustomSelect
                name="owner_operator_or_lessee"
                value={values.owner_operator_or_lessee}
                onChange={handleChange}
                fullWidth
                displayEmpty
                placeholder="Select one"
                options={[
                  { label: "Owner Operator", value: "Owner Operator" },
                  { label: "Lessee", value: "Lessee" },
                ]}
              />
            </Grid>
          </Grid>

          <LabelCard heading="Use of Vehicle" />

          <Grid container spacing={1}>
            <Grid item xs={12} lg={2}>
              <CustomFormLabel>Vehicle Use</CustomFormLabel>

              <ControlledCustomSelect
                fullWidth
                value={values.vehicle_use}
                name="vehicle_use"
                onChange={handleChange}
                placeholder="Select one"
                displayEmpty
                options={AUTOMOBILE_CSIO_VEHICLE_USE_REVERSE.map((template) => {
                  return { label: template, value: template };
                })}
                error={errors.vehicle_use ? true : false}
                helperText={errors.vehicle_use}
              ></ControlledCustomSelect>
            </Grid>

            <Grid item xs={12} lg={2}>
              <CustomFormLabel>Annual Distance</CustomFormLabel>
              <CustomTextField
                variant="outlined"
                size="small"
                type="number"
                fullWidth
                name="annual_distance"
                value={values.annual_distance}
                onChange={handleChange}
                error={errors.annual_distance ? true : false}
                helperText={errors.annual_distance}
              />
            </Grid>

            <Grid item xs={12} lg={2}>
              <CustomFormLabel>Radius of Operation</CustomFormLabel>
              <ExtendableSelect
                placeholder="Select one"
                value={values.radius_of_operation}
                options={[
                  "35",
                  "40",
                  "50",
                  "60",
                  "80",
                  "90",
                  "120",
                  "160",
                  "200",
                  "250",
                  "400",
                  "400+",
                ].map((policyType) => {
                  return { label: policyType, value: policyType };
                })}
                onChange={(newValue) => {
                  setFieldValue("radius_of_operation", newValue);
                }}
              />
            </Grid>

            <Grid item xs={12} lg={2}>
              <CustomFormLabel>Commute One-way (Kms)</CustomFormLabel>
              <CustomTextField
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                name="commute"
                value={values.commute}
                onChange={handleChange}
                error={errors.commute ? true : false}
                helperText={errors.commute}
              />
            </Grid>

            <Grid item xs={12} lg={2}>
              <CustomFormLabel>Annual kms (Business)</CustomFormLabel>
              <CustomTextField
                variant="outlined"
                size="small"
                type="number"
                fullWidth
                name="annual_kms"
                value={values.annual_kms}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} lg={2}>
              <CustomFormLabel>Business Use %</CustomFormLabel>
              <CustomTextField
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                name="business_use_percentage"
                value={values.business_use_percentage}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          <LabelCard heading="Driver Assignment" />

          <Grid container spacing={1}>
            <Grid item xs={12} lg={3}>
              <CustomFormLabel>Driver Assignment Primary</CustomFormLabel>
              <ControlledCustomSelect
                variant="outlined"
                size="small"
                type="string"
                name="driver_assignment_primary"
                value={Number(values.driver_assignment_primary)}
                onChange={handleChange}
                displayEmpty
                fullWidth
                placeholder="Select one"
                options={driverDropDown}
                error={errors.driver_assignment_primary ? true : false}
                helperText={errors.driver_assignment_primary}
              />
            </Grid>
            <Grid item xs={12} lg={3}>
              <CustomFormLabel>Driver Assignment Occassional</CustomFormLabel>
              <ControlledCustomSelect
                variant="outlined"
                size="small"
                type="string"
                name="driver_assignment_occassional"
                value={Number(values.driver_assignment_occassional)}
                onChange={handleChange}
                displayEmpty
                fullWidth
                placeholder="Select one"
                options={driverDropDown}
              />
            </Grid>
            <Grid item xs={12} lg={3}>
              <CustomFormLabel>Driver Assignment Secondary One</CustomFormLabel>
              <ControlledCustomSelect
                variant="outlined"
                size="small"
                type="string"
                name="driver_assignment_secondary_one"
                value={Number(values.driver_assignment_secondary_one)}
                onChange={handleChange}
                displayEmpty
                fullWidth
                placeholder="Select one"
                options={driverDropDown}
              />
            </Grid>

            <Grid item xs={12} lg={3}>
              <CustomFormLabel>Driver Assignment Secondary Two</CustomFormLabel>
              <ControlledCustomSelect
                variant="outlined"
                size="small"
                type="string"
                name="driver_assignment_secondary_two"
                value={Number(values.driver_assignment_secondary_two)}
                onChange={handleChange}
                displayEmpty
                fullWidth
                placeholder="Select one"
                options={driverDropDown}
              />
            </Grid>
          </Grid>

          <LabelCard heading="Vehicle - Rate Groups/Class" />

          <Grid container spacing={1}>
            <Grid item xs={12} lg={2}>
              <CustomFormLabel>Coll/AP</CustomFormLabel>
              <CustomTextField
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                name="rate_groups_or_class_coll_or_ap"
                value={values.rate_groups_or_class_coll_or_ap}
                onChange={handleChange}
                error={errors.rate_groups_or_class_coll_or_ap ? true : false}
                helperText={errors.rate_groups_or_class_coll_or_ap}
              />
            </Grid>
            <Grid item xs={12} lg={2}>
              <CustomFormLabel>Comp/SP</CustomFormLabel>
              <CustomTextField
                name="rate_groups_or_class_comp_or_sp"
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                value={values.rate_groups_or_class_comp_or_sp}
                onChange={handleChange}
                error={errors.rate_groups_or_class_comp_or_sp ? true : false}
                helperText={errors.rate_groups_or_class_comp_or_sp}
              />
            </Grid>
            <Grid item xs={12} lg={2}>
              <CustomFormLabel>AB</CustomFormLabel>
              <CustomTextField
                variant="outlined"
                size="small"
                type="string"
                fullWidth
                name="rate_groups_or_class_ab"
                value={values.rate_groups_or_class_ab}
                onChange={handleChange}
                error={errors.rate_groups_or_class_ab ? true : false}
                helperText={errors.rate_groups_or_class_ab}
              />
            </Grid>

            <Grid item xs={12} lg={2}>
              <CustomFormLabel>DCPD</CustomFormLabel>
              <CustomTextField
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                name="rate_groups_or_class_dcpd"
                value={values.rate_groups_or_class_dcpd}
                onChange={handleChange}
                error={errors.rate_groups_or_class_dcpd ? true : false}
                helperText={errors.rate_groups_or_class_dcpd}
              />
            </Grid>

            <Grid item xs={12} lg={2}>
              <CustomFormLabel>TP</CustomFormLabel>
              <CustomTextField
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                name="rate_groups_or_class_tp"
                value={values.rate_groups_or_class_tp}
                onChange={handleChange}
                error={errors.rate_groups_or_class_tp ? true : false}
                helperText={errors.rate_groups_or_class_tp}
              />
            </Grid>

            <Grid item xs={12} lg={2}>
              <CustomFormLabel>Rating Class</CustomFormLabel>
              <CustomTextField
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                name="rate_groups_or_class_rating_class"
                value={values.rate_groups_or_class_rating_class}
                onChange={handleChange}
                error={errors.rate_groups_or_class_rating_class ? true : false}
                helperText={errors.rate_groups_or_class_rating_class}
              />
            </Grid>
          </Grid>

          <LabelCard heading="Driving Record" />

          <Grid container spacing={1}>
            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>BI</CustomFormLabel>
              <CustomTextField
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                name="driving_record_bi"
                value={values.driving_record_bi}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>PD</CustomFormLabel>
              <CustomTextField
                name="driving_record_pd"
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                value={values.driving_record_pd}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>AB</CustomFormLabel>
              <CustomTextField
                variant="outlined"
                size="small"
                type="string"
                fullWidth
                name="driving_record_ab"
                value={values.driving_record_ab}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>DCPD</CustomFormLabel>
              <CustomTextField
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                name="driving_record_dcpd"
                value={values.driving_record_dcpd}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Coll/AP</CustomFormLabel>
              <CustomTextField
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                name="driving_record_coll_or_ap"
                value={values.driving_record_coll_or_ap}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          <LabelCard heading="Anti-Theft Devices" />

          <Grid container spacing={1}>
            <Grid item xs={12} lg={4}>
              <CustomFormLabel>Anti-Theft Devices</CustomFormLabel>
              <ControlledCustomSelect
                fullWidth
                value={values.anti_theft_devices}
                name="anti_theft_devices"
                onChange={handleChange}
                placeholder="Select one"
                displayEmpty
                options={CSIO_ANTI_THEFT_DEVICE_CODE_LIST.map((template) => {
                  return { label: template, value: template };
                })}
                error={errors.anti_theft_devices ? true : false}
                helperText={errors.anti_theft_devices}
              ></ControlledCustomSelect>
            </Grid>

            <Grid item xs={12} lg={4}>
              <CustomFormLabel>
                Anti-Theft Engraving Device MFR Code
              </CustomFormLabel>
              <ControlledCustomSelect
                fullWidth
                value={values.anti_theft_engraving_dev_mfr_cd}
                name="anti_theft_engraving_dev_mfr_cd"
                onChange={handleChange}
                placeholder="Select one"
                displayEmpty
                options={CSIO_ANTI_THEFT_ENGRAVING_DEV_MFR_CD.map(
                  (template) => {
                    return { label: template, value: template };
                  }
                )}
                error={errors.anti_theft_engraving_dev_mfr_cd ? true : false}
                helperText={errors.anti_theft_engraving_dev_mfr_cd}
              ></ControlledCustomSelect>
            </Grid>
            <Grid item xs={12} lg={4}></Grid>

            <Grid item xs={12} lg={4}>
              <CustomFormLabel>Device Type Code One</CustomFormLabel>
              <ExtendableSelect
                placeholder="Select one"
                value={values.device_type_code_one}
                options={type_code_options.map((template) => {
                  return { label: template.label, value: template.value };
                })}
                onChange={(newValue) => {
                  setFieldValue("device_type_code_one", newValue);
                }}
                errorMessage={errors.device_type_code_one}
                error={errors.device_type_code_one ? true : false}
              />
            </Grid>

            <Grid item xs={12} lg={4}>
              <CustomFormLabel>Device Type Code Two</CustomFormLabel>
              <ExtendableSelect
                placeholder="Select one"
                value={values.device_type_code_two}
                options={type_code_options.map((template) => {
                  return { label: template.label, value: template.value };
                })}
                onChange={(newValue) => {
                  setFieldValue("device_type_code_two", newValue);
                }}
              />
            </Grid>

            <Grid item xs={12} lg={4}>
              <CustomFormLabel>Device Type Code Three</CustomFormLabel>
              <ExtendableSelect
                placeholder="Select one"
                value={values.device_type_code_three}
                options={type_code_options.map((template) => {
                  return { label: template.label, value: template.value };
                })}
                onChange={(newValue) => {
                  setFieldValue("device_type_code_three", newValue);
                }}
              />
            </Grid>

            <Grid item xs={12} lg={4}>
              <CustomFormLabel>Product Type Code One</CustomFormLabel>
              <ExtendableSelect
                placeholder="Select one"
                value={values.product_type_code_one}
                options={product_type_options.map((template) => {
                  return { label: template.label, value: template.value };
                })}
                onChange={(newValue) => {
                  setFieldValue("product_type_code_one", newValue);
                }}
              />
            </Grid>

            <Grid item xs={12} lg={4}>
              <CustomFormLabel>Product Type Code Two</CustomFormLabel>
              <ExtendableSelect
                placeholder="Select one"
                value={values.product_type_code_two}
                options={product_type_options.map((template) => {
                  return { label: template.label, value: template.value };
                })}
                onChange={(newValue) => {
                  setFieldValue("product_type_code_two", newValue);
                }}
              />
            </Grid>

            <Grid item xs={12} lg={4}>
              <CustomFormLabel>Product Type Code Three</CustomFormLabel>
              <ExtendableSelect
                placeholder="Select one"
                value={values.product_type_code_three}
                options={product_type_options.map((template) => {
                  return { label: template.label, value: template.value };
                })}
                onChange={(newValue) => {
                  setFieldValue("product_type_code_three", newValue);
                }}
              />
            </Grid>

            <Grid item xs={12} lg={4}>
              <CustomFormLabel>Characteristics Code One</CustomFormLabel>
              <ExtendableSelect
                placeholder="Select one"
                value={values.characteristics_code_one}
                options={["Option 1", "Option 2"].map((policyType) => {
                  return { label: policyType, value: policyType };
                })}
                onChange={(newValue) => {
                  setFieldValue("characteristics_code_one", newValue);
                }}
              />
            </Grid>

            <Grid item xs={12} lg={4}>
              <CustomFormLabel>Characteristics Code Two</CustomFormLabel>
              <ExtendableSelect
                placeholder="Select one"
                value={values.characteristics_code_two}
                options={["Option 1", "Option 2"].map((policyType) => {
                  return { label: policyType, value: policyType };
                })}
                onChange={(newValue) => {
                  setFieldValue("characteristics_code_two", newValue);
                }}
              />
            </Grid>

            <Grid item xs={12} lg={4}>
              <CustomFormLabel>Characteristics Code Three</CustomFormLabel>
              <ExtendableSelect
                placeholder="Select one"
                value={values.characteristics_code_three}
                options={["Option 1", "Option 2"].map((policyType) => {
                  return { label: policyType, value: policyType };
                })}
                onChange={(newValue) => {
                  setFieldValue("characteristics_code_three", newValue);
                }}
              />
            </Grid>
          </Grid>

          <LabelCard heading="Vehicle Modifications" />

          <Grid container spacing={1}>
            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Modification</CustomFormLabel>
              <ControlledCustomSelect
                fullWidth
                value={values.modification}
                name="modification"
                onChange={handleChange}
                placeholder="Select"
                displayEmpty
                options={[
                  { label: "Y", value: "Yes" },
                  { label: "N", value: "No" },
                ]}
              />
            </Grid>

            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Value of Modification</CustomFormLabel>
              <CurrencyTextField
                fullWidth
                value={values.value_of_modification}
                onChange={(value) => {
                  setFieldValue("value_of_modification", value);
                }}
              />
            </Grid>

            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Engine</CustomFormLabel>
              <CustomSelect
                variant="outlined"
                size="small"
                type="string"
                name="engine"
                value={values.engine}
                onChange={handleChange}
                displayEmpty
                fullWidth
                renderValue={renderDropdownValue(`Select one`)}
              >
                {["Y", "N"].map((policyType) => {
                  return <MenuItem value={policyType}>{policyType}</MenuItem>;
                })}
              </CustomSelect>
            </Grid>

            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Ground Clearance</CustomFormLabel>
              <CustomSelect
                variant="outlined"
                size="small"
                type="string"
                name="ground_clearance"
                value={values.ground_clearance}
                onChange={handleChange}
                displayEmpty
                fullWidth
                renderValue={renderDropdownValue(`Select one`)}
              >
                {["Y", "N"].map((policyType) => {
                  return <MenuItem value={policyType}>{policyType}</MenuItem>;
                })}
              </CustomSelect>
            </Grid>

            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Interior Rollcage</CustomFormLabel>
              <CustomSelect
                variant="outlined"
                size="small"
                type="string"
                name="interior_rollcage"
                value={values.interior_rollcage}
                onChange={handleChange}
                displayEmpty
                fullWidth
                renderValue={renderDropdownValue(`Select one`)}
              >
                {["Y", "N"].map((policyType) => {
                  return <MenuItem value={policyType}>{policyType}</MenuItem>;
                })}
              </CustomSelect>
            </Grid>

            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Tires</CustomFormLabel>
              <CustomSelect
                variant="outlined"
                size="small"
                type="string"
                name="tires"
                value={values.tires}
                onChange={handleChange}
                displayEmpty
                fullWidth
                renderValue={renderDropdownValue(`Select one`)}
              >
                {["Y", "N"].map((policyType) => {
                  return <MenuItem value={policyType}>{policyType}</MenuItem>;
                })}
              </CustomSelect>
            </Grid>

            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Coversion for Disabled Persons</CustomFormLabel>
              <CustomSelect
                variant="outlined"
                size="small"
                type="string"
                name="coversion_for_disabled_persons"
                value={values.coversion_for_disabled_persons}
                onChange={handleChange}
                displayEmpty
                fullWidth
                renderValue={renderDropdownValue(`Select one`)}
              >
                {["Y", "N"].map((policyType) => {
                  return <MenuItem value={policyType}>{policyType}</MenuItem>;
                })}
              </CustomSelect>
            </Grid>

            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Sound Equipment</CustomFormLabel>
              <CustomSelect
                variant="outlined"
                size="small"
                type="string"
                name="sound_equipment"
                value={values.sound_equipment}
                onChange={handleChange}
                displayEmpty
                fullWidth
                renderValue={renderDropdownValue(`Select one`)}
              >
                {["Y", "N"].map((policyType) => {
                  return <MenuItem value={policyType}>{policyType}</MenuItem>;
                })}
              </CustomSelect>
            </Grid>

            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Grid Rated</CustomFormLabel>
              <CustomSelect
                variant="outlined"
                size="small"
                type="string"
                name="grid_rated"
                value={values.grid_rated}
                onChange={handleChange}
                displayEmpty
                fullWidth
                renderValue={renderDropdownValue(`Select one`)}
              >
                {["Y", "N"].map((policyType) => {
                  return <MenuItem value={policyType}>{policyType}</MenuItem>;
                })}
              </CustomSelect>
            </Grid>
            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Number of Cylinders</CustomFormLabel>
              <CustomTextField
                name="number_of_cylinders"
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                value={values.number_of_cylinders}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>Interior Custom Seats</CustomFormLabel>
              <CustomTextField
                name="interior_custom_seats"
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                value={values.interior_custom_seats}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} lg={2.4}>
              <CustomFormLabel>No Frills</CustomFormLabel>
              <CustomSelect
                variant="outlined"
                size="small"
                type="string"
                name="no_frills"
                value={values.no_frills}
                onChange={handleChange}
                displayEmpty
                fullWidth
                renderValue={renderDropdownValue(`Select one`)}
              >
                {["Y", "N"].map((policyType) => {
                  return <MenuItem value={policyType}>{policyType}</MenuItem>;
                })}
              </CustomSelect>
            </Grid>
          </Grid>
          {!staticStatusValue && (
            <Grid container>
              <Grid item xs={12} lg={2.4}>
                <CustomFormLabel>Status</CustomFormLabel>
                <ControlledCustomSelect
                  fullWidth
                  value={values.status}
                  name="status"
                  onChange={handleChange}
                  placeholder="Select one"
                  displayEmpty
                  options={COMMON_STATUS}
                />
              </Grid>
            </Grid>
          )}
          {showOnlySaveButton && (
            <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
              <RoleBasedCustomButton
                moduleKey={MODULE_IDS.AUTOMOBILE}
                type="submit"
                disabled={loading}
                variant="contained"
                onClick={() => setButtonClicked("submit")}
              >
                Save
              </RoleBasedCustomButton>
            </Box>
          )}
          {!showOnlySaveButton && (
            <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
              <RoleBasedCustomButton
                moduleKey={MODULE_IDS.AUTOMOBILE}
                type="submit"
                disabled={loading}
                variant="contained"
                onClick={() => setButtonClicked("submit")}
              >
                Save & Exit
              </RoleBasedCustomButton>
              <RoleBasedCustomButton
                moduleKey={MODULE_IDS.AUTOMOBILE}
                type="submit"
                disabled={loading}
                variant="contained"
                onClick={() => setButtonClicked("save")}
              >
                Save & Next
              </RoleBasedCustomButton>
            </Box>
          )}
        </FieldSet>
      </form>

      <VerifyVinDialog
        open={openVerifyVin}
        vin={values.vin}
        applicationData={{
          vin: values.vin,
          year: values.year,
          make: values.make,
          model: values.model,
          body_type: values.body_type,
          vehicle_type: values.vehicle_type,
        }}
        onClose={() => setOpenVerifyVin(false)}
        onUpdate={handleVerifyVinUpdate}
      />
    </PageLoader>
  );
};
